import { useEffect, useRef, useMemo } from 'react';
import { Layers } from 'lucide-react';
import { useGetInstanceTypeLayers, Layer } from '../hooks/api';
import { useQueries } from 'react-query';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import * as THREE from 'three';
import {
  Pattern,
  parsePATFile,
  buildCompositionScene,
  SOLID_PATTERN_DATA,
  MEMBRANE_PATTERN,
  MEMBRANE_FIXED_HEIGHT,
} from '@av-platform/rendering-core';
import type { CompositionLayer } from '@av-platform/rendering-core';

type TypeMarkCompositionViewProps = {
  instanceTypeId?: string;
  externalLayers?: Layer[];
  externalLoading?: boolean;
  externalError?: any;
  width?: number;
  height?: number;
};

type LayerWithPattern = {
  layer: Layer;
  materialPattern: {
    fg_pattern_url: string | null;
    bg_pattern_url: string | null;
    fg_color_hex: string;
    bg_color_hex: string;
    fg_scale: number | null;
    bg_scale: number | null;
  } | null;
  fgPattern: Pattern | null;
  bgPattern: Pattern | null;
  relativeThickness: number;
  yPosition: number;
};

const TypeMarkCompositionView = ({
  instanceTypeId,
  externalLayers,
  externalLoading,
  externalError,
  width = 350,
  height = 350,
}: TypeMarkCompositionViewProps) => {
  // 1. ZÁKLADNÍ DATA
  const useExternalData = externalLayers !== undefined;
  const {
    data: fetchedLayers = [],
    isLoading: fetchLoading,
    error: fetchError,
  } = useGetInstanceTypeLayers(instanceTypeId || '', !useExternalData);
  const layers = useExternalData ? externalLayers : fetchedLayers;
  const isLoading = useExternalData ? externalLoading ?? false : fetchLoading;
  const error = useExternalData ? externalError : fetchError;

  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  // 2. SORTOVÁNÍ A VÝPOČET CELKOVÉ TLOUŠŤKY
  const sortedLayers = useMemo(() => {
    return [...layers].sort((a, b) => a.order - b.order);
  }, [layers]);

  // 3. PŘÍPRAVA DOTAZŮ PRO VZORY (STABILNÍ KLÍČE)
  const patternQueriesArray = useMemo(() => {
    return sortedLayers.flatMap((layer) => {
      const mp = layer.solution?.material_pattern;
      const queries = [];

      // Pro membrane_layer patterny nefetchujeme, použijeme interní
      const isMembrane = layer.function === 'membrane_layer';

      // Foreground
      queries.push({
        queryKey: ['pattern', 'fg', layer.id, mp?.fg_pattern_url || 'none'],
        queryFn: async () => {
          if (isMembrane || !mp?.fg_pattern_url) return null;
          const res = await fetch(mp.fg_pattern_url);
          if (!res.ok) throw new Error('Fetch failed');
          const text = await res.text();
          return parsePATFile(text)[0] || null;
        },
        enabled: !isMembrane && !!mp?.fg_pattern_url,
      });

      // Background
      queries.push({
        queryKey: ['pattern', 'bg', layer.id, mp?.bg_pattern_url || 'none'],
        queryFn: async () => {
          if (isMembrane || !mp?.bg_pattern_url) return null;
          const res = await fetch(mp.bg_pattern_url);
          if (!res.ok) throw new Error('Fetch failed');
          const text = await res.text();
          return parsePATFile(text)[0] || null;
        },
        enabled: !isMembrane && !!mp?.bg_pattern_url,
      });

      return queries;
    });
  }, [sortedLayers]);

  const patternQueries = useQueries(patternQueriesArray);

  const isLoadingPatterns = patternQueries.some((q) => q.isLoading);

  // Build a stable map from layer ID → { fg, bg } patterns using query keys,
  // so reordering layers never causes patterns to be assigned to the wrong layer.
  const patternByLayerId = useMemo(() => {
    const map = new Map<string, { fg: Pattern | null; bg: Pattern | null }>();
    patternQueriesArray.forEach((queryDef, i) => {
      const [, type, layerId] = queryDef.queryKey as string[];
      if (!map.has(layerId)) map.set(layerId, { fg: null, bg: null });
      const entry = map.get(layerId)!;
      if (type === 'fg') entry.fg = (patternQueries[i]?.data as Pattern) || null;
      else entry.bg = (patternQueries[i]?.data as Pattern) || null;
    });
    return map;
  }, [patternQueriesArray, patternQueries]);

  // 4. MAPOVÁNÍ DAT PRO RENDERER
  // Membrane layers get a fixed pixel height; remaining space is split proportionally.
  const layersWithPatterns = useMemo<LayerWithPattern[]>(() => {
    if (sortedLayers.length === 0) return [];

    const PADDING = 15;
    const AVAILABLE_H = height - PADDING * 2;

    // Count membrane layers and compute remaining height for proportional layers
    const membraneCount = sortedLayers.filter((l) => l.function === 'membrane_layer').length;
    const membraneTotal = membraneCount * MEMBRANE_FIXED_HEIGHT;
    const remainingHeight = Math.max(AVAILABLE_H - membraneTotal, 0);

    const nonMembraneThickness = sortedLayers.reduce(
      (sum, l) => sum + (l.function === 'membrane_layer' ? 0 : Math.max(l.thickness, 0)),
      0
    );

    let currentY = 0;
    return sortedLayers.map((layer) => {
      const isMembrane = layer.function === 'membrane_layer';

      let relativeThickness: number;
      if (isMembrane) {
        relativeThickness = AVAILABLE_H > 0 ? MEMBRANE_FIXED_HEIGHT / AVAILABLE_H : 0;
      } else {
        const proportion =
          nonMembraneThickness > 0 ? Math.max(layer.thickness, 0) / nonMembraneThickness : 0;
        relativeThickness = AVAILABLE_H > 0 ? (proportion * remainingHeight) / AVAILABLE_H : 0;
      }

      const yPos = currentY;
      currentY += relativeThickness;

      const patterns = patternByLayerId.get(layer.id);
      const mp = isMembrane ? null : layer.solution?.material_pattern || null;

      // If material_pattern exists but pattern URL is absent → solid fill
      const fgFallback = mp && !mp.fg_pattern_url ? SOLID_PATTERN_DATA : null;
      const bgFallback = mp && !mp.bg_pattern_url ? SOLID_PATTERN_DATA : null;

      return {
        layer,
        materialPattern: mp,
        fgPattern: isMembrane ? MEMBRANE_PATTERN : patterns?.fg || fgFallback,
        bgPattern: isMembrane ? null : patterns?.bg || bgFallback,
        relativeThickness,
        yPosition: yPos,
      };
    });
  }, [sortedLayers, patternByLayerId, height]);

  // 5. THREE.JS EFFECT (Před early returny!)
  useEffect(() => {
    if (!containerRef.current || layersWithPatterns.length === 0 || isLoading || isLoadingPatterns)
      return;

    const container = containerRef.current;

    // Convert layersWithPatterns to CompositionLayer[] for the shared renderer
    const compositionLayers: CompositionLayer[] = layersWithPatterns.map((lp) => ({
      order: lp.layer.order,
      thickness: lp.layer.thickness,
      function: lp.layer.function,
      is_variable: lp.layer.is_variable,
      fgPattern: lp.fgPattern,
      bgPattern: lp.bgPattern,
      fg_color_hex: lp.materialPattern?.fg_color_hex || '#000000',
      bg_color_hex: lp.materialPattern?.bg_color_hex || '#ffffff',
      fg_scale: lp.materialPattern?.fg_scale ?? null,
      bg_scale: lp.materialPattern?.bg_scale ?? null,
    }));

    const { scene, camera } = buildCompositionScene(compositionLayers, { width, height });

    const renderer = new THREE.WebGLRenderer({ antialias: true, stencil: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.innerHTML = '';
    container.appendChild(renderer.domElement);

    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      renderer.dispose();
      scene.traverse((obj: any) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m: any) => m.dispose());
          } else {
            obj.material.dispose();
          }
        }
      });
    };
  }, [layersWithPatterns, width, height, isLoading, isLoadingPatterns]);

  // 6. EARLY RETURNS (Až po všech hooku!)
  if (error) {
    return (
      <div
        style={{ width, height }}
        className="flex items-center justify-center text-xs text-neutral-500"
      >
        Chyba dat
      </div>
    );
  }

  if (isLoading || isLoadingPatterns) {
    return (
      <div style={{ width, height }} className="flex items-center justify-center">
        <SpinnerOverlay size="lg" tip="Kreslím skladbu..." />
      </div>
    );
  }

  if (layersWithPatterns.length === 0) {
    return (
      <div
        style={{ width, height }}
        className="flex flex-col items-center justify-center bg-neutral-50"
      >
        <div className="h-12 w-12 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
          <Layers size={20} className="text-neutral-400" />
        </div>
        <div className="text-sm font-medium text-neutral-700">Bez definovaných vrstev</div>
        <p className="text-xs text-neutral-500 mt-1">Přidejte první vrstvu skladby</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#fff',
      }}
    />
  );
};

export default TypeMarkCompositionView;
